import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import type { CaseStudy } from '../types';

const CaseStudiesSection = () => {
  const [activeCase, setActiveCase] = useState(0);

  const caseStudies: CaseStudy[] = [
  {
    id: "1",
    company: "TechRetail Solutions",
    industry: "E-commerce",
    challenge: "Handling 5,000+ daily customer inquiries with a small support team, leading to 24-hour response times and declining customer satisfaction.",
    solution: "Implemented AI chatbot with product catalog integration, order tracking, and intelligent routing to human agents for complex issues.",
    results: [
    {
      metric: "Response Time",
      value: "24hrs → 30sec",
      description: "Average first response time"
    },
    {
      metric: "Resolution Rate",
      value: "85%",
      description: "Queries resolved without human intervention"
    },
    {
      metric: "Cost Savings",
      value: "$45K/month",
      description: "Reduction in support costs"
    }],

    image: "https://images.unsplash.com/photo-1715635795253-b29f2d7f24bb",
    alt: "Modern e-commerce office with customer service team working at computers with multiple monitors displaying analytics dashboards",
    logo: "https://images.unsplash.com/photo-1711509424072-70600119ab46",
    logoAlt: "TechRetail Solutions company logo featuring modern geometric design in blue and white",
    testimonial: {
      quote: "The AI chatbot transformed our customer service. We're now handling 3x the volume with the same team size, and our CSAT scores have increased by 40%.",
      author: "Sarah Mitchell",
      position: "Head of Customer Experience",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e40e5bff-1763298910764.png",
      avatarAlt: "Professional headshot of Sarah Mitchell, woman with short brown hair wearing navy blue blazer smiling at camera"
    }
  },
  {
    id: "2",
    company: "HealthCare Connect",
    industry: "Healthcare",
    challenge: "Patients struggling to book appointments and get basic information, overwhelming phone lines during business hours.",
    solution: "Deployed HIPAA-compliant AI chatbot for appointment scheduling, symptom checking, and insurance verification with secure patient data handling.",
    results: [
    {
      metric: "Appointment Bookings",
      value: "70%",
      description: "Automated through chatbot"
    },
    {
      metric: "Phone Volume",
      value: "-60%",
      description: "Reduction in call center load"
    },
    {
      metric: "Patient Satisfaction",
      value: "4.8/5",
      description: "Average rating for bot interactions"
    }],

    image: "https://images.unsplash.com/photo-1734787277873-b9b25ec2196d",
    alt: "Modern healthcare clinic reception area with digital check-in kiosks and comfortable waiting room with natural lighting",
    logo: "https://images.unsplash.com/photo-1661087541175-d66bd7564ed6",
    logoAlt: "HealthCare Connect logo with medical cross symbol in teal and white color scheme",
    testimonial: {
      quote: "Our patients love the 24/7 availability. The chatbot handles routine tasks flawlessly, allowing our staff to focus on providing exceptional in-person care.",
      author: "Dr. James Chen",
      position: "Chief Medical Officer",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_105dc3373-1763300641627.png",
      avatarAlt: "Professional headshot of Dr. James Chen, Asian man with glasses wearing white medical coat smiling warmly"
    }
  },
  {
    id: "3",
    company: "FinServe Bank",
    industry: "Financial Services",
    challenge: "High volume of repetitive banking queries causing long wait times and customer frustration, especially during peak hours.",
    solution: "Integrated AI chatbot with banking systems for balance inquiries, transaction history, card management, and fraud alerts with multi-factor authentication.",
    results: [
    {
      metric: "Query Resolution",
      value: "90%",
      description: "First-contact resolution rate"
    },
    {
      metric: "Wait Time",
      value: "-85%",
      description: "Average customer wait time"
    },
    {
      metric: "NPS Score",
      value: "+32 points",
      description: "Improvement in Net Promoter Score"
    }],

    image: "https://images.unsplash.com/photo-1642647916334-82e513d9cc48",
    alt: "Modern bank branch interior with digital banking stations and sleek customer service counters with ambient lighting",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18c406350-1763713776916.png",
    logoAlt: "FinServe Bank logo featuring shield emblem with modern typography in navy blue and gold",
    testimonial: {
      quote: "The ROI was evident within the first month. Our customers get instant answers to common questions, and our team can focus on complex financial advisory services.",
      author: "Michael Rodriguez",
      position: "VP of Digital Banking",
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bfef8bd5-1763295388609.png",
      avatarAlt: "Professional headshot of Michael Rodriguez, man with dark hair wearing charcoal suit and tie with confident smile"
    }
  }];


  const currentCase = caseStudies[activeCase];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Award" size={16} />
            <span>Success Stories</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Real Results from Real Businesses
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            See how companies across industries are transforming customer service with AI chatbots
          </p>
        </div>
        
        <div className="mb-8 flex flex-wrap justify-center gap-4">
          {caseStudies.map((study, index) =>
          <button
            key={study.id}
            onClick={() => setActiveCase(index)}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-fast ${
            activeCase === index ?
            'bg-primary text-primary-foreground shadow-lg' :
            'bg-surface text-text-secondary hover:bg-card hover:text-text-primary'}`
            }>

              {study.company}
            </button>
          )}
        </div>
        
        <div className="bg-card rounded-2xl shadow-xl overflow-hidden border border-border">
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="relative h-64 lg:h-auto overflow-hidden">
              <Image
                src={currentCase.image}
                alt={currentCase.alt}
                className="w-full h-full object-cover" />

              <div className="absolute top-4 left-4 bg-card rounded-lg p-3 shadow-lg">
                <Image
                  src={currentCase.logo}
                  alt={currentCase.logoAlt}
                  className="w-16 h-16 object-contain" />

              </div>
            </div>
            
            <div className="p-8 lg:p-12">
              <div className="flex items-center space-x-2 text-sm text-text-secondary mb-4">
                <Icon name="Building2" size={16} />
                <span>{currentCase.industry}</span>
              </div>
              
              <h3 className="text-2xl font-bold text-text-primary mb-6">
                {currentCase.company}
              </h3>
              
              <div className="space-y-6 mb-8">
                <div>
                  <h4 className="text-sm font-semibold text-text-tertiary mb-2 flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-error" />
                    <span>Challenge</span>
                  </h4>
                  <p className="text-text-secondary">{currentCase.challenge}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold text-text-tertiary mb-2 flex items-center space-x-2">
                    <Icon name="Lightbulb" size={16} className="text-warning" />
                    <span>Solution</span>
                  </h4>
                  <p className="text-text-secondary">{currentCase.solution}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                {currentCase.results.map((result, index) =>
                <div key={index} className="text-center p-4 bg-surface rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">
                      {result.value}
                    </div>
                    <div className="text-xs font-medium text-text-primary mb-1">
                      {result.metric}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {result.description}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-surface rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <Image
                    src={currentCase.testimonial.avatar}
                    alt={currentCase.testimonial.avatarAlt}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0" />

                  <div className="flex-1">
                    <p className="text-text-primary italic mb-4">
                      "{currentCase.testimonial.quote}"
                    </p>
                    <div>
                      <div className="font-semibold text-text-primary">
                        {currentCase.testimonial.author}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {currentCase.testimonial.position}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <Button variant="default" size="lg" iconName="FileText" iconPosition="left">
            Download Full Case Study
          </Button>
        </div>
      </div>
    </section>);

};

export default CaseStudiesSection;